import $ from 'jquery'
import ManagePatientModal from '@/components/dashboard/liste-patients/manage-patient'
import store_patient from "@/store/mods/clinique/patients"
import ProchainRendezVous from '@/components/rendez-vous/prochain-rendez-vous'   // RDV-002
import MenuActions from '@/components/menu-actions'   // menu « ⋮ » des tableaux compacts
import { date_lisible } from '@/utils/rendez-vous'
import { telephone_affiche, titre_telephone } from '@/utils/telephone'   // C9 : téléphone du patient, sinon de l'accompagnant

// Carte « Patients » du tableau de bord (Accueil, Caisse) : même tableau compact que la carte Rendez-vous (styles partagés
// _table-compacte.scss) — Patient (« + » puis le nom seul), Sexe / âge, Téléphone (règle C9), Assurance, N° carte
// d'assurance, Actions (menu « ⋮ » : Éditer le profil, Rendez-vous). Les détails dépliés sous la ligne donnent le dossier,
// la date de naissance, l'adresse, la société souscriptrice, l'accompagnant, la personne à prévenir et le bloc Prochain
// rendez-vous. Recherche côté Clinique (nom, dossier, téléphone, n° de carte), différée à la saisie, immédiate à
// Entrée ; pagination de la Clinique (25 lignes).

export const DELAI_SAISIE = 300

export const COLONNES = [
    { key: 'patient_nom', label: 'Patient' },
    { key: 'sexe_age', label: 'Sexe / âge' },
    { key: 'telephone', label: 'Téléphone' },
    { key: 'assurance', label: 'Assurance' },
    { key: 'numero_carte_assurance', label: 'N° carte d\'assurance' },   // CAISSE-VDF
    { key: 'actions', label: 'Actions', classe: 'tc-actions' }
]

export const ACTIONS_PATIENT = [
    { id: 'editer', libelle: 'Éditer le profil' },
    { id: 'rendez_vous', libelle: 'Rendez-vous' }
]

const texte = valeur => (valeur === null || valeur === undefined ? '' : String(valeur).trim())

// La carte peut être montée hors de l'application complète : le mixin global
// d'affichage n'est pas garanti dans ce cas. Le repli garde la règle M/F.
const sexeCourt = (view, value, fallback = '') => {
    if (typeof view.formatSexeCourt === 'function') return view.formatSexeCourt(value, fallback)
    const source = value && typeof value === 'object' ? (value.specificatif || value.description || value.libelle || '') : value
    const raw = source == null ? '' : String(source).trim()
    const normalized = raw.toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (['h', 'm', 'homme', 'masculin', 'masculine', 'male'].includes(normalized)) return 'M'
    if (['f', 'femme', 'feminin', 'feminine', 'female'].includes(normalized)) return 'F'
    return raw || fallback
}

export default {
    name: 'liste-patients',
    components: {
        ManagePatientModal, ProchainRendezVous, MenuActions
    },
    props: [],
    data() {
        return {
            recherche: '', page: 1, pages: 1, total: 0, patients: [], ouverts: [],
            is_loading: false, erreur: '', sequence: 0, detruit: false, search_timer: null,
            patient_id: 0,
            toggler: 0,
            rdv_modal: false, rdv_patient_id: 0, rdv_patient_nom: ''   // RDV-002
        }
    },

    computed: {
        colonnes() {
            return COLONNES
        },

        actions_patient() {
            return ACTIONS_PATIENT
        }
    },

    mounted() {
        this.charger()
    },

    beforeDestroy() {
        this.detruit = true
        this.sequence++
        clearTimeout(this.search_timer)
        this.search_timer = null
    },

    methods: {
        // Même filtre qu'avant (sans consultations) ; le terme n'est envoyé que s'il est saisi
        charger() {
            if (this.detruit) return Promise.resolve()
            clearTimeout(this.search_timer)
            this.search_timer = null
            const sequence = ++this.sequence
            const filtre = { with_consultation: false, page: this.page }
            const terme = this.recherche.trim()
            if (terme) filtre.search_term = terme
            this.is_loading = true
            this.erreur = ''
            return store_patient.getListeAllPatient(filtre).then((reponse) => {
                if (this.detruit || sequence !== this.sequence) return
                if (!reponse || !Array.isArray(reponse.data)) throw new Error('Réponse invalide')
                this.patients = reponse.data
                this.total = Number(reponse.total) || reponse.data.length
                this.pages = Math.max(1, Number(reponse.last_page) || 1)
                this.ouverts = this.ouverts.filter(id => reponse.data.some(patient => patient.patient_id === id))
                this.is_loading = false
                // Page devenue vide (patients filtrés ou supprimés entre-temps) : dernière page existante
                if (!reponse.data.length && this.page > this.pages) {
                    this.page = this.pages
                    return this.charger()
                }
            }).catch(() => {
                if (this.detruit || sequence !== this.sequence) return
                this.patients = []
                this.total = 0
                this.is_loading = false
                this.erreur = 'Impossible de charger les patients.'
            })
        },

        // Rechargement demandé par la fiche patient après un enregistrement (@emitPatient)
        refreshListePatient() {
            return this.charger()
        },

        chercher() {
            clearTimeout(this.search_timer)
            this.search_timer = setTimeout(() => {
                this.search_timer = null
                this.page = 1
                this.charger()
            }, DELAI_SAISIE)
        },

        rechercher_maintenant() {
            this.page = 1
            return this.charger()
        },

        aller_page(page) {
            if (this.is_loading || page < 1 || page > this.pages || page === this.page) return Promise.resolve()
            this.page = page
            return this.charger()
        },

        est_ouvert(id) {
            return this.ouverts.indexOf(id) !== -1
        },

        basculer_details(id) {
            this.ouverts = this.est_ouvert(id) ? this.ouverts.filter(item => item !== id) : this.ouverts.concat([id])
        },

        id_details(patient) {
            return 'patients-details-' + this._uid + '-' + patient.patient_id
        },

        sexe_court(value) {
            return sexeCourt(this, value, '')
        },

        // sexe_court : abréviation M/F rendue par le mixin global ou son repli local.
        sexe_age(patient, sexe_court) {
            return (sexe_court || patient.sexe || '—') + ' / ' + (patient.age || '—')
        },

        // C9 : téléphone du patient, sinon celui de l'accompagnant avec la filiation (« +22890246578 (Mère) »), sinon « — »
        telephone(patient) {
            return telephone_affiche(patient, 'patient_telephone')
        },

        titre_telephone(patient) {
            return titre_telephone(patient, 'patient_telephone')
        },

        // Détails dépliés : dossier, date de naissance, adresse / quartier, société souscriptrice, accompagnant (nom,
        // filiation, téléphone), personne à prévenir (nom, téléphone) ; « — » quand l'information manque
        details(patient) {
            const naissance = texte(patient.date_naissance).substr(0, 10)
            const adresse = [texte(patient.adresse), texte(patient.quartier)].filter((valeur, index, liste) => valeur && liste.indexOf(valeur) === index).join(', ')
            const accompagnant = [texte(patient.accompagnant_nom) + (texte(patient.accompagnant_filiation) ? ' (' + texte(patient.accompagnant_filiation) + ')' : ''), texte(patient.accompagnant_telephone)].filter(valeur => valeur).join(' · ')
            const a_prevenir = [texte(patient.personne_a_prevenir_nom), texte(patient.personne_a_prevenir_telephone)].filter(valeur => valeur).join(' · ')
            return [
                ['N° dossier', texte(patient.patient_code)],
                ['Date de naissance', /^\d{4}-\d{2}-\d{2}$/.test(naissance) ? date_lisible(naissance) : ''],
                ['Adresse / quartier', adresse],
                ['Société souscriptrice', texte(patient.souscripteur)],
                ['Accompagnant', accompagnant],
                ['Personne à prévenir', a_prevenir]
            ].map(detail => [detail[0], detail[1] || '—'])
        },

        agir(patient, action) {
            if (!patient || !patient.patient_id) return
            if (action === 'editer') this.setPatientToEdit(patient.patient_id)
            else if (action === 'rendez_vous') this.ouvrir_rendez_vous(patient)
        },

        // Fiche patient (fenêtre Bootstrap de ManagePatientModal) : chargement par le toggler puis ouverture
        setPatientToEdit(patient_id) {
            this.patient_id = parseInt(patient_id)
            this.toggler = (this.toggler == 0) ? 1 : 0
            if (typeof $ === 'function') {
                const fenetre = $('.edit-profil-dashboard')
                if (fenetre && typeof fenetre.modal === 'function') fenetre.modal('show')
            }
        },

        // RDV-002 : rendez-vous du patient (fenêtre)
        ouvrir_rendez_vous(item) {
            this.rdv_patient_id = parseInt(item.patient_id)
            this.rdv_patient_nom = item.patient_nom || ''
            this.rdv_modal = true
        }
    }
}
